import { Button, ExecuteButton, FileUploader, Form, Text } from '@/components';
import { VITE_API_URL } from '@/config';
import { fetchPaymentGroupUploaderTemplate } from '@/features/project/_utils/uploader-template';
import { useMessage } from '@/providers';
import { createCsv, downloadCsv, downloadURL } from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';

type Props = {
  projectId: string;
  onSubmit: (file: File) => void;
};

export const PaymentGroupUploadForm: FC<Props> = ({ projectId, onSubmit }) => {
  const { openMessage } = useMessage();
  const { t } = useTranslation();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const onExportTemplateFile = async () => {
    setIsDownloading(true);
    try {
      const templateData = await fetchPaymentGroupUploaderTemplate(projectId);
      if (templateData.csv.length === 0) {
        await openMessage('error', t('error-message-csv-export-empty-data'));
        return;
      }
      downloadCsv(
        createCsv({
          headerKeys: templateData.csv[0] ?? [],
          headerConverter: {},
          body: [],
        }),
        t('payment-group-uploader-template'),
      );
    } catch {
      await openMessage('error', t('error-message-file-download-failed'));
    } finally {
      setIsDownloading(false);
    }
  };

  const onExportSampleFile = async () => {
    setIsDownloading(true);
    try {
      downloadURL(
        `${VITE_API_URL}/docs/payment-group-uploader-sample.xlsm`,
        `${t('payment-group-uploader-sample')}.xlsm`,
      );
    } catch {
      await openMessage('error', t('error-message-file-download-failed'));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Form
      leavePageAlertCondition={null}
      layout="vertical"
      onFinish={async () => {
        invariant(uploadedFile, t('unreachable-code'));
        onSubmit(uploadedFile);
      }}
    >
      <Form.Item>
        <Button
          onClick={onExportSampleFile}
          loading={isDownloading}
          type="primary"
          style={{ height: '200%' }}
        >
          <Text color="white" text={t('download-sample')} />
        </Button>
      </Form.Item>
      <Form.Item>
        <Button
          onClick={onExportTemplateFile}
          loading={isDownloading}
          type="primary"
        >
          {t('download-template')}
        </Button>
      </Form.Item>

      <Form.Item>
        <FileUploader
          files={uploadedFile ? [uploadedFile] : []}
          onChange={async (acceptedFiles) => {
            const file = acceptedFiles[0];
            if (
              file &&
              file.type !== 'text/csv' &&
              file.type !== 'application/vnd.ms-excel' &&
              file.type !== 'application/octet-stream'
            ) {
              await openMessage('error', t('error-message-invalid-file-type'));
              return;
            }
            setUploadedFile(file ?? null);
          }}
          handleCancel={() => {
            setUploadedFile(null);
          }}
          maxFiles={1}
        />
      </Form.Item>

      <ExecuteButton disabled={!uploadedFile} />
    </Form>
  );
};
